import { useEffect, useMemo } from 'react'
import { sortBy } from 'lodash'

import { useAppDispatch } from '@/utils/typeHelpers'
import { formatCurrency } from '@/utils/currencyHelpers'

import { selectEmployees, selectPayrollByUuid } from '../../payroll.selectors'
import { fetchEmployees, fetchSinglePayroll } from '../../payrollActions'
import { generatePayrollDetails } from '../../helpers'
import { Table, Text, Accordion } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { TypographyStyles } from '@/styles'

export const PayrollDetails = ({
  uuid,
  title = 'Payroll Details',
}: {
  uuid: string
  title?: string
}) => {
  const dispatch = useAppDispatch()
  const upcomingPayroll = useReselector(selectPayrollByUuid, uuid)

  const employees = useReselector(selectEmployees)

  useEffect(() => {
    dispatch(
      fetchSinglePayroll(uuid, {
        include: ['benefits', 'deductions', 'taxes'],
      })
    )
    dispatch(fetchEmployees())
  }, [dispatch, uuid])

  const details = useMemo(
    () => generatePayrollDetails(upcomingPayroll, employees),
    [employees, upcomingPayroll]
  )

  if (!upcomingPayroll || !details) {
    return null
  }

  const {
    totals: {
      employeeTaxes,
      companyTaxes,
      employeeNetPay,
      reimbursements,
      garnishments,
      grossPay,
      deductions,
      employeeBenefits,
      companyNetPay,
      companyBenefits,
      allTaxes,
    },
  } = details

  return (
    <div className="flex flex-col gap-2">
      <Text variant={TypographyStyles.H2} className="pt-4">
        {title}
      </Text>
      <Accordion type="single" collapsible variant="text">
        <Accordion.Item value="tax-details">
          <Accordion.Trigger className="mb-0">
            <Text variant={TypographyStyles.BODY_MD}>
              What gets taxed and debited
            </Text>
          </Accordion.Trigger>
          <Accordion.Content>
            <div className="flex flex-col gap-6">
              {/* Taxes Table */}
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Tax Description</Table.HeaderCell>
                    <Table.HeaderCell className="text-right">
                      By Your Employees
                    </Table.HeaderCell>
                    <Table.HeaderCell className="text-right">
                      By Your Company
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {Object.values(details.taxes || {}).map(
                    ({ name, employeeAmount, companyAmount }) => (
                      <Table.Row key={name}>
                        <Table.Cell>{name}</Table.Cell>
                        <Table.Cell className="text-right">
                          {employeeAmount
                            ? formatCurrency(employeeAmount)
                            : 'N/A'}
                        </Table.Cell>
                        <Table.Cell className="text-right">
                          {companyAmount
                            ? formatCurrency(companyAmount)
                            : 'N/A'}
                        </Table.Cell>
                      </Table.Row>
                    )
                  )}
                  <Table.Row>
                    <Table.Cell className="text-green-600">
                      <b>Totals</b>
                    </Table.Cell>
                    <Table.Cell className="text-right text-green-600">
                      {formatCurrency(employeeTaxes)}
                    </Table.Cell>
                    <Table.Cell className="text-right text-green-600">
                      <b>{formatCurrency(companyTaxes)}</b>
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>

              {/* Debits Table */}
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Debited by Gusto</Table.HeaderCell>
                    <Table.HeaderCell className="text-right">
                      Total
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell>Direct Deposits</Table.Cell>
                    <Table.Cell className="text-right">
                      {formatCurrency(employeeNetPay)}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Reimbursements</Table.Cell>
                    <Table.Cell className="text-right">
                      {formatCurrency(reimbursements)}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>Garnishments</Table.Cell>
                    <Table.Cell className="text-right">
                      {formatCurrency(garnishments)}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell className="text-green-600">
                      <b>Taxes (Employees and Employers)</b>
                    </Table.Cell>
                    <Table.Cell className="text-right">
                      {formatCurrency(allTaxes)}
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </div>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>

      <Accordion type="single" collapsible variant="text">
        <Accordion.Item value="employee-take-home">
          <Accordion.Trigger className="mb-0">
            <Text variant={TypographyStyles.BODY_MD}>
              What your employees worked and take home
            </Text>
          </Accordion.Trigger>
          <Accordion.Content>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    Employees (
                    {upcomingPayroll.employee_compensations?.length ?? 0})
                  </Table.HeaderCell>
                  <Table.HeaderCell>Payment Type</Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Gross Pay
                  </Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Deductions
                  </Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Reimbursements
                  </Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Employee Taxes
                  </Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Employee Benefits
                  </Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Payment
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sortBy(Object.values(details.employees), 'name').map(
                  ({
                    uuid,
                    name,
                    paymentType,
                    grossPay,
                    deductions,
                    reimbursements,
                    employeeTaxes,
                    employeeBenefits,
                    employeeNetPay,
                  }) => (
                    <Table.Row key={`takeHome${uuid}`}>
                      <Table.Cell>{name}</Table.Cell>
                      <Table.Cell>{paymentType}</Table.Cell>
                      <Table.Cell className="text-right">
                        {formatCurrency(grossPay)}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {formatCurrency(deductions)}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {formatCurrency(reimbursements)}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {formatCurrency(employeeTaxes)}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {formatCurrency(employeeBenefits)}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {formatCurrency(employeeNetPay)}
                      </Table.Cell>
                    </Table.Row>
                  )
                )}
                <Table.Row>
                  <Table.Cell className="text-green-600">
                    <b>Totals</b>
                  </Table.Cell>
                  <Table.Cell />
                  <Table.Cell className="text-right">
                    {formatCurrency(grossPay)}
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    {formatCurrency(deductions)}
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    {formatCurrency(reimbursements)}
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    {formatCurrency(employeeTaxes)}
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    {formatCurrency(employeeBenefits)}
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    {formatCurrency(employeeNetPay)}
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>

      <Accordion type="single" collapsible variant="text">
        <Accordion.Item value="company-pays">
          <Accordion.Trigger className="mb-0">
            <Text variant={TypographyStyles.BODY_MD}>
              What your company pays
            </Text>
          </Accordion.Trigger>
          <Accordion.Content>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    Employees (
                    {upcomingPayroll.employee_compensations?.length ?? 0})
                  </Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Gross Pay
                  </Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Reimbursements
                  </Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Company Taxes
                  </Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Company Benefits
                  </Table.HeaderCell>
                  <Table.HeaderCell className="text-right">
                    Subtotal
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sortBy(Object.values(details.employees), 'name').map(
                  ({
                    uuid,
                    name,
                    grossPay,
                    reimbursements,
                    companyTaxes,
                    companyBenefits,
                    companyNetPay,
                  }) => (
                    <Table.Row key={`takeHome${uuid}`}>
                      <Table.Cell>{name}</Table.Cell>
                      <Table.Cell className="text-right">
                        {formatCurrency(grossPay)}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {formatCurrency(reimbursements)}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {formatCurrency(companyTaxes)}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {formatCurrency(companyBenefits)}
                      </Table.Cell>
                      <Table.Cell className="text-right">
                        {formatCurrency(companyNetPay)}
                      </Table.Cell>
                    </Table.Row>
                  )
                )}
                <Table.Row>
                  <Table.Cell className="text-green-600">
                    <b>Totals</b>
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    {formatCurrency(grossPay)}
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    {formatCurrency(reimbursements)}
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    {formatCurrency(companyTaxes)}
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    {formatCurrency(companyBenefits)}
                  </Table.Cell>
                  <Table.Cell className="text-right text-green-600">
                    <b>{formatCurrency(companyNetPay)}</b>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
    </div>
  )
}
